import "@app/App.css";
import { Dialog, DialogTrigger } from "@comps/ui/dialog";
import { Notification } from "@comps/ui/notification";
import { FullRightAccess, hasGlobalRightAccess, ObjectRightType, ViewRightType } from "@dto/Users";
import { getPathKeys, getShownPathKeys } from "@utils/model";
import { countHiddenFields, fieldEntries, hasAggregationOverrides } from "@utils/organizeFields";
import { TW_ObjectHeaderButtonTitle, TW_ObjectModelViewHeaderButton, TW_ObjectModelViewHeaderIcon } from "@utils/twClasses";
import { Columns3 } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDataStore, useModelStore, useUserStore } from "src/store";
import OrganizeFieldsDialogContent, { OrganizeFieldsMode } from "./OrganizeFieldsDialogContent";

// Button of the header of a view which opens the "Organize fields" dialog: the
// fields of the view are reordered, shown or hidden and their aggregation is
// changed there. A dot tells the view hides fields or changes aggregations.
// In the configuration of an object (no view), the dialog reorders the fields
// of the object; the button then never has a dot.
function OrganizeFieldsButton() {

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // The dialog cannot be closed while the view is being saved (the view is
  // saved anyway once the request is sent)
  const savingRef = useRef(false);
  const selectedView = useModelStore((state) => state.selectedView);
  const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
  const relationByKeys = useModelStore((state) => state.relationByKeys);
  const fieldByKeys = useModelStore((state) => state.fieldByKeys);
  const userRole = useModelStore((state) => state.userRole);
  const user = useUserStore((state) => state.user);
  const rowActionsEnabled = useDataStore(state => state.rowActionsEnabled);
  const { t } = useTranslation();

  const fields = selectedObjectInfos?.fields || null;
  const entries = useMemo(() => {
    if (!fields) {
      return [];
    }
    return fieldEntries(fields, getPathKeys(selectedObjectInfos, fields, relationByKeys, fieldByKeys, true));
  }, [selectedObjectInfos, fields, relationByKeys, fieldByKeys]);
  const shownPathKeys = getShownPathKeys(selectedView, entries.map(entry => entry.pathKey));
  const isModified = countHiddenFields(entries, selectedView, shownPathKeys) > 0 || hasAggregationOverrides(entries, selectedView);

  const mode: OrganizeFieldsMode = selectedView ? 'view' : 'object';
  // Changing the view needs the full right on the view; reordering the fields of
  // the object, the full right on the object (as the update of a field)
  const hasFullAccess = mode === 'view'
    ? selectedView?.ownerKey === user?._key || hasGlobalRightAccess(user, userRole, ViewRightType, FullRightAccess, selectedView?._key)
    : hasGlobalRightAccess(user, userRole, ObjectRightType, FullRightAccess, selectedObjectInfos?.object?._key);
  const isDisabled = (!fields || fields.length === 0) || !rowActionsEnabled;
  const title = t('object.header.button.organize.fields.title');
  // The title is not displayed on narrow screens, and the dot is not read: the label carries both
  const label = isModified ? t('object.header.button.organize.fields.modified') : title;

  return (
    <Dialog open={isDialogOpen} onOpenChange={(open) => {
      if (open || !savingRef.current) {
        setIsDialogOpen(open);
      }
    }}>
      <DialogTrigger
        disabled={isDisabled}
        aria-label={label}
        className={TW_ObjectModelViewHeaderButton + (isDisabled ? " opacity-50 cursor-not-allowed" : " opacity-100 cursor-default")}
      >
        <div className="flex flex-row justify-start items-center relative">
          <Columns3 className={TW_ObjectModelViewHeaderIcon} strokeWidth={1.5} />
          {isModified && <Notification number={null} />}
        </div>
        <span className={TW_ObjectHeaderButtonTitle}>{title}</span>
      </DialogTrigger>
      {/* Mounted at each opening: the draft starts from the view as it is then */}
      {isDialogOpen &&
        <OrganizeFieldsDialogContent
          mode={mode}
          entries={entries}
          shownPathKeys={shownPathKeys}
          readOnly={!hasFullAccess}
          onSavingChange={(saving) => { savingRef.current = saving; }}
          onClose={() => setIsDialogOpen(false)}
        />}
    </Dialog>
  )
}

export default OrganizeFieldsButton;
